import { CheckCircle, Send } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { api } from '../../core/trpc'

type ContactFormValues = {
    name: string
    email: string
    phoneNumber: string
    message: string
}

type ContactFormProps = {
    mutation: 'sendContactForm' | 'sendExpertContactForm'
    messagePlaceholder?: string
}

export const ContactForm = ({
    mutation,
    messagePlaceholder = 'Írja le kérdését vagy üzenetét...',
}: ContactFormProps) => {
    const [submitted, setSubmitted] = useState(false)
    const { mutateAsync, isPending } = api.notifications[mutation].useMutation()

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<ContactFormValues>({
        defaultValues: {
            name: '',
            email: '',
            phoneNumber: '',
            message: '',
        },
    })

    const onSubmit = async (data: ContactFormValues) => {
        await mutateAsync(data)
        setSubmitted(true)
    }

    if (submitted) {
        return (
            <div className="flex flex-col items-center justify-center gap-6 rounded-[2rem] border border-primary/10 bg-white p-12 shadow-xl shadow-primary/5 text-center">
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
                    <CheckCircle className="h-10 w-10 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-brand-dark">Üzenet elküldve!</h2>
                <p className="text-brand-gray">
                    Köszönjük megkeresését. Hamarosan felvesszük Önnel a kapcsolatot.
                </p>
            </div>
        )
    }

    return (
        <form
            className="flex flex-col gap-5 rounded-[2rem] border border-slate-100 bg-white p-10 shadow-xl shadow-slate-100"
            onSubmit={handleSubmit(onSubmit)}
        >
            <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-brand-dark">Név</label>
                <input
                    {...register('name', { required: true })}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-6 py-4 text-brand-dark shadow-sm transition-all placeholder:text-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Az Ön neve"
                />
                {errors.name && <span className="text-xs text-red-500">Ez a mező kötelező</span>}
            </div>

            <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-brand-dark">Telefonszám</label>
                <input
                    {...register('phoneNumber', { required: true })}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-6 py-4 text-brand-dark shadow-sm transition-all placeholder:text-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="+36 30 123 4567"
                    type="tel"
                />
                {errors.phoneNumber && (
                    <span className="text-xs text-red-500">Ez a mező kötelező</span>
                )}
            </div>

            <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-brand-dark">Email cím</label>
                <input
                    {...register('email', {
                        required: true,
                        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    })}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-6 py-4 text-brand-dark shadow-sm transition-all placeholder:text-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="pelda@email.com"
                    type="email"
                />
                {errors.email && (
                    <span className="text-xs text-red-500">
                        {errors.email.type === 'pattern'
                            ? 'Érvényes email-t adjon meg'
                            : 'Ez a mező kötelező'}
                    </span>
                )}
            </div>

            <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-brand-dark">Üzenet</label>
                <textarea
                    {...register('message', { required: true })}
                    className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-6 py-4 text-brand-dark shadow-sm transition-all placeholder:text-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder={messagePlaceholder}
                    rows={5}
                />
                {errors.message && <span className="text-xs text-red-500">Ez a mező kötelező</span>}
            </div>

            <button
                type="submit"
                disabled={isPending}
                className="mt-2 flex items-center justify-center gap-3 rounded-2xl bg-primary px-8 py-5 text-base font-bold text-white shadow-xl shadow-primary/20 transition-all hover:bg-primary/90 disabled:opacity-60"
            >
                {isPending ? 'Küldés...' : 'Üzenet küldése'}
                <Send className="h-5 w-5" />
            </button>
        </form>
    )
}
